/**
 * Einheitlicher Header für Unterseiten. Die Startseite behält ihren eigenen Hero-Header.
 */
import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X } from "lucide-react";
import { useLocale } from "@/contexts/LocaleContext";
import { PRIMARY_NAV_LINKS, SECONDARY_NAV_LINKS } from "@/lib/siteNavigation";
import LanguageSelector from "./LanguageSelector";

const NAV_LINKS = PRIMARY_NAV_LINKS;
const MORE_LINKS = SECONDARY_NAV_LINKS;

const MOBILE_NAV_LINKS = [
  ...NAV_LINKS,
  { key: "blog", href: "/blog" },
] as const;

export default function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [location] = useLocation();
  const { locale, setLocale, t } = useLocale();
  const isActive = (href: string) => location === href;

  useEffect(() => {
    document.documentElement.classList.toggle("mobile-menu-open", mobileOpen);
    return () => document.documentElement.classList.remove("mobile-menu-open");
  }, [mobileOpen]);

  return (
    <nav className="bg-white/95 shadow-sm sticky top-0 z-[80] lg:backdrop-blur-sm">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between lg:h-20">
          <div className="flex min-w-0 items-center gap-3">
            <Link href="/">
              <div className="group flex min-w-0 cursor-pointer flex-col leading-tight">
                <span className="truncate text-xl font-bold tracking-wide transition-colors group-hover:text-[#631172]" style={{ fontFamily: "'Playfair Display', serif", color: "#2d1b4e" }}>Immozwerg</span>
                <span className="truncate text-[10px] tracking-[0.2em]" style={{ color: "#8a6a20" }}>Andrea Heinz · HAJ Immo e.K.</span>
              </div>
            </Link>
            <div className="hidden lg:block"><LanguageSelector compact /></div>
          </div>

          <div className="hidden lg:flex items-center gap-3">
            {NAV_LINKS.map(item => (
              <Link key={item.href} href={item.href}>
                <span className={`whitespace-nowrap text-xs font-medium tracking-wide transition-colors hover:text-[#8a6a20] cursor-pointer ${isActive(item.href) ? "text-[#631172] font-bold" : "text-[#2d1b4e]"}`}>
                  {t(item.key)}
                </span>
              </Link>
            ))}
            <div className="relative group">
              <button className="min-h-11 px-2 text-sm font-medium tracking-wide text-[#2d1b4e] hover:text-[#8a6a20] transition-colors flex items-center gap-1">{t("more")} ▾</button>
              <div className="absolute top-full right-0 mt-2 w-44 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                {MORE_LINKS.map(link => (
                  <Link key={link.href} href={link.href}>
                    <div className={`px-4 py-3 text-sm font-medium hover:bg-[#FAF8F5] hover:text-[#631172] cursor-pointer transition-colors border-b border-gray-50 last:border-0 ${isActive(link.href) ? "text-[#631172] font-bold" : "text-[#2d1b4e]"}`}>{t(link.key)}</div>
                  </Link>
                ))}
              </div>
            </div>
            <Link href="/kontakt"><button className="whitespace-nowrap rounded-full px-3 py-2.5 text-sm font-bold text-white transition-all duration-200 hover:scale-105" style={{ background: "linear-gradient(135deg, #2d1b4e, #4a2d7a)" }}>{t("contact")}</button></Link>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <div className="absolute right-4 top-full z-[90] mt-2 lg:hidden"><LanguageSelector compact /></div>
            <button className="w-11 h-11 inline-flex items-center justify-center text-[#2d1b4e]" aria-label={mobileOpen ? t("closeMenu") : t("openMenu")} onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {locale !== "de" && (
        <div className="border-t border-[#e6dcd2] bg-[#fffaf1] px-4 py-2 text-center text-xs leading-relaxed text-[#2d1b4e]" role="status">
          <span>{t("translationNotice")} </span>
          <button type="button" onClick={() => setLocale("de")} className="font-bold underline decoration-[#8a6a20] underline-offset-2 hover:text-[#631172] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#631172]">
            {t("showGermanOriginal")}
          </button>
        </div>
      )}

      {mobileOpen && (
        <div className={`immozwerg-mobile-menu fixed inset-x-0 bottom-0 ${locale === "de" ? "top-16" : "top-28"} z-[80] overflow-y-auto overscroll-contain border-t border-gray-100 bg-white shadow-lg lg:hidden`} role="dialog" aria-modal="true" aria-label={t("openMenu")}>
          <div className="min-h-full px-5 pb-28 pt-5">
            {MOBILE_NAV_LINKS.map(item => (
              <Link key={item.href} href={item.href}>
                <div onClick={() => setMobileOpen(false)} className={`text-left min-h-14 py-4 border-b border-gray-50 font-medium transition-colors cursor-pointer ${isActive(item.href) ? "text-[#631172]" : "text-[#2d1b4e] hover:text-[#8a6a20]"}`}>{t(item.key)}</div>
              </Link>
            ))}
            <div className="pt-4 border-t border-gray-100 mt-2">
              <p className="text-xs font-bold tracking-widest uppercase text-gray-400 mb-2">{t("otherPages")}</p>
              {MORE_LINKS.filter(link => link.key !== "blog" && link.key !== "contact").map(link => (
                <Link key={link.href} href={link.href}>
                  <div onClick={() => setMobileOpen(false)} className={`text-left min-h-14 py-4 border-b border-gray-50 font-medium transition-colors cursor-pointer ${isActive(link.href) ? "text-[#631172] font-bold" : "text-[#4a2d7a] hover:text-[#8a6a20]"}`}>{t(link.key)}</div>
                </Link>
              ))}
            </div>
            <div className="pt-3"><Link href="/kontakt"><button onClick={() => setMobileOpen(false)} className="w-full min-h-11 py-3 rounded-full font-bold text-white text-sm" style={{ background: "linear-gradient(135deg, #2d1b4e, #4a2d7a)" }}>{t("contact")}</button></Link></div>
          </div>
        </div>
      )}
    </nav>
  );
}
